import { useQuery } from "@tanstack/react-query";
import { ChevronDown, Search, SlidersHorizontal, X } from "lucide-react";
import { useEffect, useRef, useState } from "react";

import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuCheckboxItem,
  DropdownMenuContent,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Input } from "@/components/ui/input";
import {
  type BoardFilters as Filters,
  type CompletedAgeBucket,
} from "@/lib/board";
import { boardFacetsOptions } from "@/lib/queries";
import { shouldCommitSearch } from "@/lib/searchCommit";
import { STOPLIGHT } from "@/lib/stoplight";
import { useDebouncedValue } from "@/lib/useDebouncedValue";
import { cn } from "@/lib/utils";

// Single-select "completed within the last …" ranges — cumulative/nested
// (Grafana-style relative ranges), newest first. The dropdown is titled
// "Completed" so the options stay short.
const COMPLETED_BANDS: { value: CompletedAgeBucket; label: string }[] = [
  { value: "24h", label: "Last 24 hours" },
  { value: "7d", label: "Last 7 days" },
  { value: "30d", label: "Last 30 days" },
  { value: "90d", label: "Last 90 days" },
  { value: "180d", label: "Last 180 days" },
];

const bandLabel = (b: CompletedAgeBucket): string =>
  COMPLETED_BANDS.find((o) => o.value === b)?.label ?? b;

// Multi-select review-priority levels, highest first (matches the board sort).
const PRIORITY_OPTIONS: {
  value: number;
  label: string;
  marks: string;
  color: string;
}[] = [
  { value: 1, label: "Notable", marks: "!", color: STOPLIGHT.green },
  { value: 2, label: "Important", marks: "!!", color: STOPLIGHT.amber },
  { value: 3, label: "Critical", marks: "!!!", color: STOPLIGHT.red },
];
const priorityLabel = (p: number): string =>
  PRIORITY_OPTIONS.find((o) => o.value === p)?.label ?? String(p);

/** A removable summary of one active selection. */
function FilterChip({ label, onRemove }: { label: string; onRemove: () => void }) {
  return (
    <span className="inline-flex items-center gap-1 rounded-md border border-border bg-muted/50 py-0.5 pl-2 pr-1 text-xs text-foreground">
      <span className="max-w-[12rem] truncate">{label}</span>
      <button
        type="button"
        onClick={onRemove}
        aria-label={`Remove ${label} filter`}
        className="cursor-pointer rounded-sm p-0.5 text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
      >
        <X className="size-3" />
      </button>
    </span>
  );
}

/**
 * Selected-count pill on a filter trigger. When `hidden` (the search is focused
 * and the triggers shrink) it fades AND collapses its width — animating in step
 * with the trigger resize instead of vanishing instantly, and freeing its space
 * so the label isn't truncated.
 */
function CountBadge({ value, hidden }: { value: number; hidden: boolean }) {
  return (
    <span
      className={cn(
        "shrink-0 overflow-hidden rounded-full bg-primary text-center text-[10px] font-semibold leading-4 text-primary-foreground transition-all duration-300 ease-[cubic-bezier(0.32,0.72,0,1)]",
        hidden
          ? "ml-0 max-w-0 px-0 opacity-0"
          : "ml-1.5 max-w-[2rem] px-1.5 opacity-100",
      )}
    >
      {value}
    </span>
  );
}

/** A multi-select facet dropdown. Toggling an option keeps the menu open. */
function FacetDropdown({
  label,
  options,
  selected,
  onChange,
  widthClass,
  hideCount,
}: {
  label: string;
  options: string[];
  selected: string[];
  onChange: (next: string[]) => void;
  widthClass: string;
  /** Hide the selected-count badge (the triggers shrink while searching). */
  hideCount: boolean;
}) {
  const count = selected.length;
  const toggle = (value: string) =>
    onChange(
      selected.includes(value)
        ? selected.filter((v) => v !== value)
        : [...selected, value],
    );

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          variant="outline"
          size="sm"
          className={cn("h-8 justify-between gap-1.5", widthClass)}
          disabled={options.length === 0}
        >
          <span className="flex min-w-0 items-center">
            <span className="truncate">{label}</span>
            {count > 0 && <CountBadge value={count} hidden={hideCount} />}
          </span>
          <ChevronDown className="size-3.5 shrink-0 text-muted-foreground" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="start" className="max-h-80 w-56 overflow-y-auto">
        <DropdownMenuLabel className="flex items-center justify-between">
          {label}
          {count > 0 && (
            <button
              type="button"
              onClick={() => onChange([])}
              className="cursor-pointer text-xs font-normal text-muted-foreground hover:text-foreground"
            >
              Clear
            </button>
          )}
        </DropdownMenuLabel>
        <DropdownMenuSeparator />
        {options.map((value) => (
          <DropdownMenuCheckboxItem
            key={value}
            checked={selected.includes(value)}
            // Keep the menu open so several options can be toggled at once.
            onSelect={(e) => e.preventDefault()}
            onCheckedChange={() => toggle(value)}
          >
            {value}
          </DropdownMenuCheckboxItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

/**
 * Free-text title search. Holds the input locally and debounces commits so we
 * don't refetch every column on each keystroke; external resets (Clear all)
 * flow back in through `value`.
 */
function QuickSearch({
  value,
  onChange,
  onFocusChange,
}: {
  value: string;
  onChange: (v: string) => void;
  onFocusChange: (focused: boolean) => void;
}) {
  const [term, setTerm] = useState(value);
  const debounced = useDebouncedValue(term, 300);
  const onChangeRef = useRef(onChange);
  onChangeRef.current = onChange;
  // The last debounced term we acted on. Comparing against THIS (not `value`) is
  // what stops an external reset from re-committing stale text — see
  // shouldCommitSearch.
  const prevDebounced = useRef(value);

  // External resets (Clear all / back-nav) flow back in via `value`.
  useEffect(() => setTerm(value), [value]);
  // Commit the debounced term only when the user's input actually changed.
  useEffect(() => {
    if (shouldCommitSearch(debounced, prevDebounced.current, value)) {
      onChangeRef.current(debounced);
    }
    prevDebounced.current = debounced;
  }, [debounced, value]);

  // Press "/" anywhere (outside a field) to jump to search. (shadcn Input isn't
  // ref-forwarded, so focus it by id.)
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== "/") return;
      const el = document.activeElement as HTMLElement | null;
      const tag = el?.tagName;
      if (tag === "INPUT" || tag === "TEXTAREA" || el?.isContentEditable) return;
      e.preventDefault();
      document.getElementById("board-search")?.focus();
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, []);

  return (
    <div className="relative w-full">
      <Search className="pointer-events-none absolute left-2.5 top-1/2 size-4 -translate-y-1/2 text-muted-foreground" />
      <Input
        id="board-search"
        value={term}
        onChange={(e) => setTerm(e.target.value)}
        onFocus={() => onFocusChange(true)}
        onBlur={() => onFocusChange(false)}
        placeholder="Search tasks…"
        className="h-8 pl-8 pr-8"
      />
      {term ? (
        <button
          type="button"
          onClick={() => setTerm("")}
          aria-label="Clear search"
          className="absolute right-1.5 top-1/2 -translate-y-1/2 cursor-pointer rounded-sm p-0.5 text-muted-foreground transition-colors hover:text-foreground"
        >
          <X className="size-3.5" />
        </button>
      ) : (
        <kbd className="pointer-events-none absolute right-2 top-1/2 -translate-y-1/2 rounded border border-border px-1 text-[10px] font-medium text-muted-foreground">
          /
        </kbd>
      )}
    </div>
  );
}

/**
 * The board's filter bar. Stateless: filters come in via props (URL-driven in
 * Dashboard) and changes flow back through `onChange`. Facet options are
 * fetched here and cached by TanStack Query.
 */
export function BoardFilters({
  filters,
  onChange,
  spaceId,
}: {
  filters: Filters;
  onChange: (next: Filters) => void;
  spaceId: string;
}) {
  const { data: facets } = useQuery(boardFacetsOptions(spaceId));

  // Focusing the search grows it on the right while the facet triggers yield a
  // little width — one synchronized layout transition. The search is flex-1, so
  // it fills the space the triggers vacate frame-for-frame (the butter).
  const [searchFocused, setSearchFocused] = useState(false);
  const triggerWidth = (rest: string, focused: string) =>
    cn(
      "shrink-0 transition-[width] duration-300 ease-[cubic-bezier(0.32,0.72,0,1)]",
      searchFocused ? focused : rest,
    );

  // Flatten every active selection into one removable-chip list, in trigger
  // order (project → requested by → assignee → completed). Each chip is prefixed
  // with its dimension so they're unambiguous — Assignee and Requested-by are
  // both person names, indistinguishable without the label.
  const chips: { key: string; label: string; onRemove: () => void }[] = [
    ...filters.lists.map((v) => ({
      key: `list:${v}`,
      label: `Project: ${v}`,
      onRemove: () =>
        onChange({ ...filters, lists: filters.lists.filter((x) => x !== v) }),
    })),
    ...filters.creators.map((v) => ({
      key: `creator:${v}`,
      label: `Requested by: ${v}`,
      onRemove: () =>
        onChange({
          ...filters,
          creators: filters.creators.filter((x) => x !== v),
        }),
    })),
    ...filters.assignees.map((v) => ({
      key: `assignee:${v}`,
      label: `Assignee: ${v}`,
      onRemove: () =>
        onChange({
          ...filters,
          assignees: filters.assignees.filter((x) => x !== v),
        }),
    })),
    ...(filters.completedAge
      ? [
          {
            key: "completedAge",
            label: `Completed: ${bandLabel(filters.completedAge)}`,
            onRemove: () => onChange({ ...filters, completedAge: null }),
          },
        ]
      : []),
    ...filters.priorities.map((p) => ({
      key: `priority:${p}`,
      label: `Priority: ${priorityLabel(p)}`,
      onRemove: () =>
        onChange({
          ...filters,
          priorities: filters.priorities.filter((x) => x !== p),
        }),
    })),
  ];

  return (
    <div className="space-y-3">
      <div className="flex items-center gap-2">
        <span className="flex shrink-0 items-center gap-1.5 pr-1 text-sm font-medium text-muted-foreground">
          <SlidersHorizontal className="size-4" />
          Filters
        </span>
        <FacetDropdown
          label="Project"
          options={facets?.lists ?? []}
          selected={filters.lists}
          onChange={(lists) => onChange({ ...filters, lists })}
          widthClass={triggerWidth("w-32", "w-28")}
          hideCount={searchFocused}
        />
        <FacetDropdown
          label="Requested by"
          options={facets?.creators ?? []}
          selected={filters.creators}
          onChange={(creators) => onChange({ ...filters, creators })}
          widthClass={triggerWidth("w-40", "w-36")}
          hideCount={searchFocused}
        />
        <FacetDropdown
          label="Assignee"
          options={facets?.assignees ?? []}
          selected={filters.assignees}
          onChange={(assignees) => onChange({ ...filters, assignees })}
          widthClass={triggerWidth("w-32", "w-28")}
          hideCount={searchFocused}
        />

        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button
              variant="outline"
              size="sm"
              className={cn(
                "h-8 justify-between gap-1.5",
                triggerWidth("w-36", "w-32"),
              )}
            >
              <span className="flex min-w-0 items-center">
                <span className="truncate">Completed</span>
                {filters.completedAge && (
                  <CountBadge value={1} hidden={searchFocused} />
                )}
              </span>
              <ChevronDown className="size-3.5 shrink-0 text-muted-foreground" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="start" className="w-48">
            <DropdownMenuLabel className="flex items-center justify-between">
              Completed
              {filters.completedAge && (
                <button
                  type="button"
                  onClick={() => onChange({ ...filters, completedAge: null })}
                  className="cursor-pointer text-xs font-normal text-muted-foreground hover:text-foreground"
                >
                  Clear
                </button>
              )}
            </DropdownMenuLabel>
            <DropdownMenuSeparator />
            {COMPLETED_BANDS.map((o) => (
              <DropdownMenuCheckboxItem
                key={o.value}
                checked={filters.completedAge === o.value}
                onCheckedChange={(checked) =>
                  onChange({
                    ...filters,
                    completedAge: checked ? o.value : null,
                  })
                }
              >
                {o.label}
              </DropdownMenuCheckboxItem>
            ))}
          </DropdownMenuContent>
        </DropdownMenu>

        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button
              variant="outline"
              size="sm"
              className={cn(
                "h-8 justify-between gap-1.5",
                triggerWidth("w-32", "w-28"),
              )}
            >
              <span className="flex min-w-0 items-center">
                <span className="truncate">Priority</span>
                {filters.priorities.length > 0 && (
                  <CountBadge
                    value={filters.priorities.length}
                    hidden={searchFocused}
                  />
                )}
              </span>
              <ChevronDown className="size-3.5 shrink-0 text-muted-foreground" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="start" className="w-44">
            <DropdownMenuLabel className="flex items-center justify-between">
              Priority
              {filters.priorities.length > 0 && (
                <button
                  type="button"
                  onClick={() => onChange({ ...filters, priorities: [] })}
                  className="cursor-pointer text-xs font-normal text-muted-foreground hover:text-foreground"
                >
                  Clear
                </button>
              )}
            </DropdownMenuLabel>
            <DropdownMenuSeparator />
            {PRIORITY_OPTIONS.map((o) => (
              <DropdownMenuCheckboxItem
                key={o.value}
                checked={filters.priorities.includes(o.value)}
                onSelect={(e) => e.preventDefault()}
                onCheckedChange={() =>
                  onChange({
                    ...filters,
                    priorities: filters.priorities.includes(o.value)
                      ? filters.priorities.filter((x) => x !== o.value)
                      : [...filters.priorities, o.value],
                  })
                }
              >
                <span className="flex items-center gap-1.5">
                  {/* Fixed-width marks column so the labels line up; sized to snugly
                      fit "!!!" (left-aligned) so the gap to the text stays tight. */}
                  <span
                    style={{ color: o.color }}
                    className="w-4 shrink-0 font-extrabold leading-none"
                  >
                    {o.marks}
                  </span>
                  {o.label}
                </span>
              </DropdownMenuCheckboxItem>
            ))}
          </DropdownMenuContent>
        </DropdownMenu>

        <div className="min-w-0 flex-1">
          <QuickSearch
            value={filters.search}
            onChange={(search) => onChange({ ...filters, search })}
            onFocusChange={setSearchFocused}
          />
        </div>
      </div>

      {chips.length > 0 && (
        <div className="flex flex-wrap items-center gap-2">
          {chips.map((c) => (
            <FilterChip key={c.key} label={c.label} onRemove={c.onRemove} />
          ))}
          <Button
            variant="ghost"
            size="sm"
            className="ml-auto h-7 gap-1.5 text-muted-foreground"
            onClick={() =>
              onChange({
                lists: [],
                assignees: [],
                creators: [],
                completedAge: null,
                priorities: [],
                search: "",
              })
            }
          >
            <X className="size-3.5" />
            Clear all
          </Button>
        </div>
      )}
    </div>
  );
}
